import { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { toggleSetValue } from "@heraion/shared";

import { Button } from "../primitives/Button";
import { ChipMultiRow } from "../primitives/ChipMultiRow";
import { ChoiceTray } from "../primitives/ChoiceTray";
import { Dialog, DialogScroll } from "../primitives/Dialog";
import type { UiTheme } from "../theme";

// «Copy onto…» of the day menu and of the round menu (#336): the target
// picked in a centred dialog over the editor, then Cancel. The menu that
// opens it has closed first, so it is never a modal over a modal. The
// confirm that follows is the caller's. With `multiple` it is the copy to
// SEVERAL days (2B: the exercise menu's «Copy to other days» and the
// selection bar's «Copy to…»): the days ticked as chips, then the confirm
// button, which says how many. The round menu's «Type» picks in it too (4d):
// `selected` marks the type the round has. Mounted by the caller while open.
export function CopyOntoTray({
  theme,
  question,
  options,
  footnote,
  onChoose,
  onCancel,
  multiple,
}: {
  theme: UiTheme;
  question: string;
  /** Every target but the source itself. `selected`: where things are now. */
  options: { key: string; label: string; a11yLabel?: string; selected?: boolean }[];
  footnote: string;
  /** One target: the tap is the choice. Unused with `multiple`. */
  onChoose?: (key: string) => void;
  onCancel: () => void;
  multiple?: {
    /** The button's text for this many ticked, "Copy to 2 days". */
    confirmLabel: (count: number) => string;
    onConfirm: (keys: string[]) => void;
  };
}) {
  const { t } = useTranslation();
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());
  const styles = makeStyles(theme);
  // Button's own hitSlop and web floor take Cancel to 44.
  const cancel = (
    <Button theme={theme} variant="ghost" label={t("program.cancel", { defaultValue: "Cancel" })} onPress={onCancel} />
  );
  if (!multiple) {
    return (
      <Dialog theme={theme} visible onRequestClose={onCancel} title={question}>
        <DialogScroll style={styles.scroll}>
          <ChoiceTray bare theme={theme} options={options} onChoose={(k) => onChoose?.(k)} footnote={footnote} />
        </DialogScroll>
        <View style={styles.actions}>{cancel}</View>
      </Dialog>
    );
  }
  const labels = new Map(options.map((o) => [o.key, o]));
  // In the options' order, whatever order they were ticked in.
  const chosen = options.map((o) => o.key).filter((k) => ticked.has(k));
  return (
    <Dialog theme={theme} visible onRequestClose={onCancel} title={question}>
      <DialogScroll style={styles.scroll} contentContainerStyle={styles.body}>
        <ChipMultiRow
          theme={theme}
          groupA11yLabel={question}
          choices={options.map((o) => o.key)}
          selected={ticked}
          onToggle={(k) => setTicked((cur) => toggleSetValue(cur, k))}
          label={(k) => labels.get(k)?.label ?? k}
          chipA11yLabel={(k) => labels.get(k)?.a11yLabel ?? labels.get(k)?.label ?? k}
        />
        <Text style={styles.footnote}>{footnote}</Text>
      </DialogScroll>
      <View style={styles.actions}>
        {cancel}
        <Button
          theme={theme}
          icon="copy"
          label={multiple.confirmLabel(chosen.length)}
          disabled={chosen.length === 0}
          onPress={() => multiple.onConfirm(chosen)}
        />
      </View>
    </Dialog>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    // DialogScroll floors its height at 120dp (for long lists on short phones); a few chips
    // do not need it, so size to the content and let maxHeight alone cap it.
    scroll: { minHeight: 0 },
    body: { gap: s.s3 },
    footnote: { color: p.ink2, fontSize: ty.sizes.sm },
    actions: { flexDirection: "row", flexWrap: "wrap", justifyContent: "flex-end", gap: s.s3 },
  });
}
